import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'src/components/Modals/Modal';
import { useNotification } from 'src/components/Notification/useNotfication';
import { useKeyboard } from 'src/view/hooks/useKeyboard/useKeyboard';

import type { MessageAttachmentView } from './MessageItem';

type Props = {
    attachment: MessageAttachmentView;
    attachments?: MessageAttachmentView[];
    initialIndex?: number;
    onClose: () => void;
};

const resolveAttachmentType = (mimeType?: string) => {
    if (!mimeType) return 'file';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType === 'application/pdf') return 'pdf';
    return 'file';
};

const SWIPE_THRESHOLD_PX = 50;

const getImageFileName = (attachment: MessageAttachmentView) => {
    const name = (attachment.name || attachment.id || 'message-image').replace(
        /[\\/]/g,
        '-',
    );

    if (/\.[a-z0-9]{2,5}$/i.test(name)) return name;

    const extension = attachment.mimeType
        ?.split('/')[1]
        ?.replace('jpeg', 'jpg');
    return extension ? `${name}.${extension}` : name;
};

export const MessageAttachmentPreviewModal = ({
    attachment,
    attachments,
    initialIndex,
    onClose,
}: Props) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [showModal, setShowModal] = useState(true);
    const [isDownloading, setIsDownloading] = useState(false);
    const swipeStart = useRef<{ x: number; y: number } | null>(null);

    const galleryAttachments =
        attachments?.length && initialIndex !== undefined
            ? attachments
            : [attachment];
    const [currentIndex, setCurrentIndex] = useState(initialIndex ?? 0);
    const activeAttachment = galleryAttachments[currentIndex] ?? attachment;
    const type = resolveAttachmentType(activeAttachment.mimeType);
    const title = activeAttachment.name || activeAttachment.id;
    const canBrowse = galleryAttachments.length > 1;
    const imagePositionLabel = canBrowse
        ? `${currentIndex + 1} / ${galleryAttachments.length}`
        : undefined;

    useEffect(() => {
        setCurrentIndex(initialIndex ?? 0);
    }, [attachment.id, initialIndex]);

    const showPrevious = () => {
        setCurrentIndex(current =>
            current === 0 ? galleryAttachments.length - 1 : current - 1,
        );
    };

    const showNext = () => {
        setCurrentIndex(current =>
            current === galleryAttachments.length - 1 ? 0 : current + 1,
        );
    };

    const startSwipe = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!canBrowse || type !== 'image') return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;

        swipeStart.current = { x: event.clientX, y: event.clientY };
    };

    const finishSwipe = (event: React.PointerEvent<HTMLDivElement>) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (!start) return;

        const horizontalDistance = event.clientX - start.x;
        const verticalDistance = event.clientY - start.y;
        if (
            Math.abs(horizontalDistance) < SWIPE_THRESHOLD_PX ||
            Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
        ) {
            return;
        }

        if (horizontalDistance > 0) {
            showPrevious();
        } else {
            showNext();
        }
    };

    const downloadActiveImage = async () => {
        if (type !== 'image' || isDownloading) return;

        setIsDownloading(true);
        try {
            const response = await fetch(activeAttachment.url);
            if (!response.ok) throw new Error('Image download failed');

            const imageBlob = await response.blob();
            const objectUrl = URL.createObjectURL(imageBlob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = getImageFileName(activeAttachment);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
            notify(t('modules.messages.attachmentsImageSaved'));
        } catch {
            notify(t('modules.messages.attachmentsImageSaveError'), true);
        } finally {
            setIsDownloading(false);
        }
    };

    const keyboardProps = useKeyboard({
        Escape: () => setShowModal(false),
        ArrowLeft: canBrowse ? showPrevious : undefined,
        ArrowRight: canBrowse ? showNext : undefined,
    });

    const body = useMemo(() => {
        if (type === 'image') {
            return (
                <img
                    src={activeAttachment.url}
                    alt={title}
                    draggable={false}
                    className="max-h-[calc(100vh-11rem)] max-w-full object-contain rounded-md shadow-2xl sm:max-h-[calc(100vh-13rem)]"
                />
            );
        }

        if (type === 'video') {
            return (
                <video
                    src={activeAttachment.url}
                    controls
                    className="max-h-[calc(100vh-11rem)] max-w-full rounded-md bg-black shadow-2xl sm:max-h-[calc(100vh-13rem)]"
                />
            );
        }

        if (type === 'pdf') {
            return (
                <iframe
                    title={title}
                    src={activeAttachment.url}
                    className="h-[calc(100vh-11rem)] w-full rounded-md bg-white sm:h-[calc(100vh-13rem)]"
                />
            );
        }

        return (
            <div className="rounded-lg bg-white p-4 text-sm text-mongooseGray shadow-2xl">
                <p className="font-semibold text-woodyBrown">{title}</p>
                <p className="mt-2">
                    {t('modules.messages.attachmentsHeading')}
                </p>
            </div>
        );
    }, [activeAttachment.url, t, title, type]);

    return (
        <Modal
            show={showModal}
            className="fixed inset-0 z-70 flex bg-black/80 p-0 sm:items-center sm:justify-center sm:p-6"
            onClose={onClose}
            onOutsideClick={() => setShowModal(false)}
        >
            <div
                {...keyboardProps}
                role="dialog"
                aria-modal="true"
                className="flex h-full w-full flex-col outline-none sm:h-auto sm:max-h-[calc(100vh-3rem)] sm:max-w-6xl"
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-white/10 bg-woodyBrown px-4 py-3 shadow-lg sm:rounded-t-xl">
                    <div className="min-w-0">
                        <p className="truncate font-semibold text-lightPeach">
                            {title}
                        </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                        {imagePositionLabel ? (
                            <span className="rounded-full bg-lightPeach/15 px-2.5 py-1 text-xs font-semibold text-lightPeach">
                                {imagePositionLabel}
                            </span>
                        ) : null}
                        {type === 'image' ? (
                            <button
                                type="button"
                                aria-label={t(
                                    'modules.messages.attachmentsSaveImage',
                                )}
                                title={t(
                                    'modules.messages.attachmentsSaveImage',
                                )}
                                disabled={isDownloading}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-lightPeach text-woodyBrown shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-lightPeach/50 disabled:cursor-wait disabled:opacity-60"
                                onClick={() => void downloadActiveImage()}
                            >
                                <DownloadIcon />
                            </button>
                        ) : null}
                        <button
                            type="button"
                            aria-label="Close"
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-lightPeach text-woodyBrown shadow-sm hover:bg-white focus:outline-none focus:ring-2 focus:ring-lightPeach/50"
                            onClick={() => setShowModal(false)}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="20"
                                height="20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M18 6L6 18"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="flex min-h-0 flex-1 flex-col bg-[#151313] sm:rounded-b-xl">
                    <div
                        data-testid="message-attachment-gallery"
                        className="relative flex min-h-0 flex-1 touch-pan-y select-none items-center justify-center p-3 sm:p-6"
                        onPointerDown={startSwipe}
                        onPointerUp={finishSwipe}
                        onPointerCancel={() => {
                            swipeStart.current = null;
                        }}
                    >
                        {canBrowse ? (
                            <>
                                <GalleryNavigationButton
                                    ariaLabel={t(
                                        'modules.messages.attachmentsPreviousImage',
                                    )}
                                    className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 sm:inline-flex"
                                    direction="previous"
                                    onClick={showPrevious}
                                />
                                <GalleryNavigationButton
                                    ariaLabel={t(
                                        'modules.messages.attachmentsNextImage',
                                    )}
                                    className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 sm:inline-flex"
                                    direction="next"
                                    onClick={showNext}
                                />
                            </>
                        ) : null}
                        {body}
                    </div>
                    {canBrowse ? (
                        <div className="flex shrink-0 items-center justify-between gap-4 border-t border-white/10 bg-woodyBrown px-5 py-3 sm:hidden">
                            <GalleryNavigationButton
                                ariaLabel={t(
                                    'modules.messages.attachmentsPreviousImage',
                                )}
                                direction="previous"
                                onClick={showPrevious}
                            />
                            {imagePositionLabel ? (
                                <span className="text-sm font-semibold text-lightPeach">
                                    {imagePositionLabel}
                                </span>
                            ) : null}
                            <GalleryNavigationButton
                                ariaLabel={t(
                                    'modules.messages.attachmentsNextImage',
                                )}
                                direction="next"
                                onClick={showNext}
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
};

const DownloadIcon = () => (
    <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M12 4V15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path
            d="M8 11L12 15L16 11"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M5 20H19"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
);

const GalleryNavigationButton = ({
    ariaLabel,
    className = '',
    direction,
    onClick,
}: {
    ariaLabel: string;
    className?: string;
    direction: 'previous' | 'next';
    onClick: () => void;
}) => {
    const path =
        direction === 'previous' ? 'M15 18L9 12L15 6' : 'M9 18L15 12L9 6';

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            title={ariaLabel}
            className={`inline-flex h-12 w-12 items-center justify-center rounded-full bg-lightPeach text-woodyBrown shadow-xl hover:bg-white focus:outline-none focus:ring-2 focus:ring-lightPeach/50 ${className}`}
            onClick={onClick}
        >
            <svg
                viewBox="0 0 24 24"
                width="26"
                height="26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d={path}
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </button>
    );
};
